import { isIOS } from './platformHelpers';
import { UIManager } from '../native/UIManager';
import { callPhoneNumber } from './phoneCallHelpers';
import { differenceInMinutes } from 'date-fns';
import { DriverBookingData, RentalData } from '../@types';
import { isRentalOngoing } from './rentalHelpers';
import { LocalizePropType } from '../HoC/withTextLocalizer';
import {
  injectedActionSheet,
  injectedDeviceEmitter,
  injectedIntercom,
  injectedLinking,
  injectedPermissionsManager,
} from '../globals/ConfigureGlobals';
import { isWeb } from './isWeb';
import { getBrandName } from './brandHelpers';
import { injectedStore } from '../globals/StoreAccess';

export const getIsRentingUser = (
  xBrandId: string,
  rentalData: RentalData,
  bookings: Array<DriverBookingData>,
  lastFinishedRentalDate: Date
) =>
  isRentalOngoing(rentalData) ||
  bookings?.filter((booking) =>
    // @ts-ignore
    ['confirmed', 'offered'].includes(booking?.status)
  ).length > 0 ||
  (lastFinishedRentalDate &&
    differenceInMinutes(new Date(), new Date(lastFinishedRentalDate)) < 15);

const displayMessenger = () => {
  // Ensure custom_brand is set before presenting Intercom
  if (injectedIntercom) {
    try {
      const xBrandId = injectedStore?.getState?.()?.config?.xBrandId;
      const brandName = getBrandName(xBrandId);
      const customBrand = (
        brandName && brandName.length ? brandName : 'getaway'
      ).toUpperCase();
      injectedIntercom.updateUser({
        customAttributes: { custom_brand: customBrand },
      });
    } catch {
      // no-op
    }
  }
  injectedPermissionsManager?.requestNotificationPermission(() => {
    injectedIntercom.present();
  });
};

const displayIntercomByMode = (
  intercomHandlingMode: 'always' | 'onLogin' | 'onContactSupport' | 'never',
  supportContacted: boolean,
  isAuthorizedUser: boolean
) => {
  if (intercomHandlingMode === 'always') {
    displayMessenger();
  } else if (intercomHandlingMode === 'onContactSupport') {
    if (!supportContacted) {
      injectedDeviceEmitter?.emit('setSupportContacted');
    }
    displayMessenger();
  } else if (intercomHandlingMode === 'onLogin' && isAuthorizedUser) {
    displayMessenger();
  }
};

// Customer support channels are:
// 1- Text support channels: whats app, intercom, email
// 2- Phone support
//
// Renting User = User has on going rental or Has an active booking or Its been 15min since last rental
//
// Business logic:
// If phoneSupportType == always
//   - open dialog with first option phone and second Text always
// If phoneSupportType == duringTrip
//   - If renting user, open dialog with first option phone and second Text
//   - If not renting user, open text support / no dialog
//
// Intercom handling modes:
//   - always: intercom always accessible, user always registered on new sessions
//   - onContactSupport:  intercom always accessible, register user to intercom on new sessions if he contacted support before
//   - onLogin: show/enable intercom when user has logged in only
//   - never: ...

export const helpAction = async (
  isRentingUser: boolean,
  localize: LocalizePropType,
  intercomHandlingMode: 'always' | 'onLogin' | 'onContactSupport' | 'never',
  supportContacted: boolean,
  isAuthorizedUser: boolean,
  _isVerifiedUser: boolean = false,
  _isRentalStarted: boolean = false,
  brandSettings: any,
  onlyTextMode: boolean
) => {
  const phoneSupportType = brandSettings?.phoneSupportType;
  const isIntercom = brandSettings?.textSupportType === 'intercom';
  const isWhatsaap = brandSettings?.textSupportType === 'whatsapp';
  const formattedServiceWhatsapp = brandSettings?.serviceWhatsapp?.includes('+')
    ? brandSettings?.serviceWhatsapp?.replace(/\s/g, '').split('+')[1]
    : brandSettings?.serviceWhatsapp?.replace(/\s/g, '');

  const textModeAction = () => {
    if (isIntercom) {
      displayIntercomByMode(
        intercomHandlingMode,
        supportContacted,
        isAuthorizedUser
      );
    } else if (isWhatsaap) {
      injectedLinking
        .canOpenURL(`https://wa.me/${formattedServiceWhatsapp}`)
        .then((supported: any) => {
          if (supported) {
            injectedLinking.openURL(
              `https://wa.me/${formattedServiceWhatsapp}`
            );
          }
        });
    } else {
      injectedLinking
        .canOpenURL(`mailto:${brandSettings?.serviceEmail}`)
        .then((supported: any) => {
          if (supported) {
            injectedLinking.openURL(`mailto:${brandSettings?.serviceEmail}`);
          }
        });
    }
  };

  if (onlyTextMode || (phoneSupportType === 'duringTrip' && !isRentingUser)) {
    textModeAction();
  } else if (
    phoneSupportType === 'always' ||
    (phoneSupportType === 'duringTrip' && isRentingUser)
  ) {
    if (isIOS()) {
      injectedActionSheet.showActionSheetWithOptions(
        {
          title: '',
          message: localize('help.dialog.title'),
          options: [
            'cancel',
            brandSettings?.servicePhone || 'customerSupport.call',
            isIntercom
              ? localize('user.action.support.text')
              : isWhatsaap
              ? localize('support.whatsapp')
              : localize('support.email'),
          ].map((key) => localize(key as any as string)),
          cancelButtonIndex: 0,
        },
        (buttonIndex: any) => {
          if (buttonIndex === 1) {
            callPhoneNumber(
              localize(
                brandSettings?.servicePhone ||
                  localize('customerSupport.number')
              )
            );
          } else if (buttonIndex === 2) {
            textModeAction();
          }
        }
      );
    } else {
      UIManager.showListAlert(
        null,
        [
          localize(brandSettings?.servicePhone || 'customerSupport.call'),
          isIntercom
            ? localize('user.action.support.text')
            : isWhatsaap
            ? localize('support.whatsapp')
            : localize('support.email'),
        ],
        (response) => {
          if (response.buttonIndex) {
            const selectedIndex = parseInt(response.buttonIndex, 10);

            if (selectedIndex === 0) {
              callPhoneNumber(
                localize(
                  brandSettings?.servicePhone ||
                    localize('customerSupport.number')
                )
              );
            } else if (selectedIndex === 1) {
              textModeAction();
            }
          }
        }
      );
    }
  }
};

// src/utils/intercom.ts
export function loadIntercomScript(appId: string, force: boolean, user: any) {
  if (!isWeb()) return;
  if (!appId) return;

  const isWidgetMode = window.location.search.indexOf('mode=widget') !== -1;
  if (isWidgetMode) return;

  const customBrand = getBrandName(process.env.VITE_X_BRAND_ID)?.toUpperCase();

  const inject = () => {
    // prevent duplicate injection
    if (document.querySelector('script[data-intercom-app-id]')) return;

    (window as any).intercomSettings = {
      api_base: 'https://api-iam.intercom.io',
      app_id: appId,
      hide_default_launcher: true,
    };

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = `https://widget.intercom.io/widget/${appId}`;
    s.setAttribute('data-intercom-app-id', appId);

    const x = document.getElementsByTagName('script')[0];
    x?.parentNode?.insertBefore(s, x);
  };

  // @ts-ignore
  if (force && !window.Intercom) {
    inject();
    setTimeout(() => {
      // @ts-ignore
      if (window?.Intercom) {
        if (user) {
          // @ts-ignore
          window?.Intercom('boot', {
            email: user.email,
            created_at: 1234567890,
            name: `${user.firstName} ${user.lastName}`,
            user_id: user.id,
            custom_user_status: user?.blocked ? 'blocked' : 'active',
            custom_block_reason: user?.blockReasonDetail,
            custom_tariff: user?.tariff?.name,
            custom_brand: customBrand,
            custom_operatorID: user.merchantSystemData?.[0]?.merchantReference
              ? `${user.merchantSystemData?.[0]?.merchantReference}_${user.merchantSystemData?.[0]?.merchantSystem}`
              : '',
          });
        } else {
          // @ts-ignore
          window?.Intercom('boot', {
            custom_brand: customBrand,
          });
        }
        setTimeout(() => {
          // @ts-ignore
          window?.Intercom('show');
        }, 300);
      }
    }, 700);
    return;
  }

  // If Cookiebot already initialized, inject now; otherwise wait for the event
  const isLocalOrStaging =
    window.location.hostname === 'localhost' ||
    window.location.hostname.includes('staging') ||
    window.location.hostname.includes('cloudfront');
  if (
    isLocalOrStaging ||
    ((window as any).Cookiebot &&
      typeof (window as any).Cookiebot.consent === 'object')
  ) {
    inject();
  } else {
    window.addEventListener('CookiebotOnLoad', inject, { once: true });
  }
}
